import { axiosClient, setUseMocks } from "@next-platform/client-common-core";
import { API_URL } from "@next-platform/client-common-core/infra/constants";
import axios, { AxiosHeaders, type RawAxiosHeaders } from "axios";

import apiClient from "@/services/apiClient";
import jsonApiService from "@/services/jsonApiService";

import readMockApiBaseUrl from "./mockApiEnv";
import { readMockApiEnabled, readMockApiKey } from "./mockStorage";

type JsonApiService = typeof jsonApiService;
type MockApiRequestConfig = {
  headers?: unknown;
  url?: string;
};

let mockApiRequestInterceptorId: number | null = null;
let mockApiDefaultRequestInterceptorId: number | null = null;
let mockApiClientRequestInterceptorId: number | null = null;

const clearMockApiKeyInterceptor = (): void => {
  if (mockApiRequestInterceptorId !== null) {
    axiosClient.interceptors.request.eject(mockApiRequestInterceptorId);
    mockApiRequestInterceptorId = null;
  }

  if (mockApiDefaultRequestInterceptorId !== null) {
    axios.interceptors.request.eject(mockApiDefaultRequestInterceptorId);
    mockApiDefaultRequestInterceptorId = null;
  }

  if (mockApiClientRequestInterceptorId !== null) {
    apiClient.interceptors.request.eject(mockApiClientRequestInterceptorId);
    mockApiClientRequestInterceptorId = null;
  }
};

const addMockApiKeyHeader = <RequestConfig extends MockApiRequestConfig>({
  config,
  mockBaseUrl,
  mockApiKey,
}: {
  config: RequestConfig;
  mockBaseUrl: string;
  mockApiKey: string;
}): RequestConfig => {
  if (!config.url?.includes(mockBaseUrl)) {
    return config;
  }

  const headers = new AxiosHeaders(config.headers as AxiosHeaders | RawAxiosHeaders | string | undefined);
  headers.set("x-api-key", mockApiKey);
  return {
    ...config,
    headers,
  } as RequestConfig;
};

const configureMockApiKeyInterceptor = ({ mockBaseUrl, mockApiKey }: { mockBaseUrl: string; mockApiKey: string }): void => {
  clearMockApiKeyInterceptor();
  if (!mockBaseUrl || !mockApiKey) {
    return;
  }

  mockApiRequestInterceptorId = axiosClient.interceptors.request.use((config) => addMockApiKeyHeader({ config, mockBaseUrl, mockApiKey }));
  mockApiDefaultRequestInterceptorId = axios.interceptors.request.use((config) => addMockApiKeyHeader({ config, mockBaseUrl, mockApiKey }));
  mockApiClientRequestInterceptorId = apiClient.interceptors.request.use((config) => addMockApiKeyHeader({ config, mockBaseUrl, mockApiKey }));
};

const configureMockApi = ({ service }: { service: JsonApiService }): void => {
  const isMockApiEnabled = readMockApiEnabled();
  const apiBaseUrl = API_URL || "";
  const mockBaseUrl = readMockApiBaseUrl();
  const shouldUseMockApi = isMockApiEnabled && Boolean(mockBaseUrl);
  const baseUrl = shouldUseMockApi ? mockBaseUrl : apiBaseUrl;

  setUseMocks(shouldUseMockApi, mockBaseUrl, apiBaseUrl);
  configureMockApiKeyInterceptor({ mockBaseUrl, mockApiKey: shouldUseMockApi ? readMockApiKey() : "" });
  service.setBaseUrl(baseUrl);
};

export default configureMockApi;
